import { parseServerTime } from './serverTime';

// Departure-session rows (getDeparturesForDate + the departureForecastUpdate WebSocket), shared by
// the Departures and On Ground tabs so both read one aircraft the same way.

// Identity of a departure session — hex + session start, NOT callsign, because the same session
// can emit forecasts with null callsign before the icao_airline lookup resolves, and a populated
// callsign after. Including callsign in the key produced duplicates of the same aircraft (one row
// with airline logo, one without). Also drops fractional seconds + timezone suffix to collapse
// HTTP-sourced (DB-truncated) and WS-sourced (in-memory nanos) representations of the same time.
export function rowKey(icao, _callsign, sessionStartAt) {
  let s = sessionStartAt || '';
  s = String(s).replace(/\.\d+/, '').replace(/Z$|[+-]\d{2}:?\d{2}$/, '');
  s = s.length > 16 ? s.slice(0, 16) : s;
  return `${icao || ''}|${s}`;
}

// Reconcile the server's state (or absence of it) with the freshness of the last ADS-B message.
// The backend writes one of POWER_ON | TAXIING | TAKE_OFF | DEPARTED | PARKED on every forecast
// row; if the freshest forecast is older than 5 min and no liftoff was observed, the aircraft is
// "stale" — either transponder off at the gate (parked) or out of receiver coverage.
export function effectiveState(row, nowMs) {
  if (row.actualLiftoffAt) return 'DEPARTED';
  if (row.state === 'PARKED') return 'PARKED';
  const lastUpd = parseServerTime(row.lastUpdateAt) || parseServerTime(row.lastForecastAt);
  if (lastUpd && nowMs - lastUpd.getTime() > 5 * 60 * 1000) return 'PARKED';
  return row.state || 'POWER_ON';
}

const ACTIVE = new Set(['POWER_ON', 'TAXIING', 'TAKE_OFF']);
export const isActiveDeparture = state => ACTIVE.has(state);

/**
 * Predicted departure order: every aircraft still on the ground with engines on, numbered by its
 * predicted liftoff (ettTime), 1 = next away. Returns rowKey -> position.
 */
export function departureOrder(rows, nowMs) {
  const active = rows
    .filter(r => isActiveDeparture(effectiveState(r, nowMs)) && parseServerTime(r.ettTime))
    .sort((a, b) => parseServerTime(a.ettTime) - parseServerTime(b.ettTime));
  const out = new Map();
  active.forEach((r, i) => out.set(rowKey(r.icaoAddress, r.callsign, r.sessionStartAt), i + 1));
  return out;
}

/** Minutes until the predicted liftoff of an active session (negative once overdue), else null. */
export function remainingTaxiMinutes(row, nowMs) {
  if (!isActiveDeparture(effectiveState(row, nowMs))) return null;
  const ett = parseServerTime(row.ettTime);
  return ett ? (ett.getTime() - nowMs) / 60000 : null;
}

/** Minutes as "4m 05s" ("-1m 10s" when overdue). */
export function formatRemainingMmSs(min) {
  if (min == null || isNaN(min)) return '-';
  const sign = min < 0 ? '-' : '';
  const abs = Math.abs(min);
  const totalSec = Math.round(abs * 60);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${sign}${h}h ${m}m ${String(s).padStart(2, '0')}s`;
  return `${sign}${m}m ${String(s).padStart(2, '0')}s`;
}

/**
 * Error of the session's first liftoff estimate: actual minus first predicted, in minutes, the same
 * sign as Final Δ (+ = left later than predicted). The server sends it; a liftoff that arrived over
 * the WebSocket is worked out here.
 */
export function firstErrorMinutes(row) {
  if (row.firstErrorMinutes != null) return row.firstErrorMinutes;
  const first = parseServerTime(row.firstEttTime);
  const lift = parseServerTime(row.actualLiftoffAt);
  return first && lift ? (lift - first) / 60000 : null;
}

/** Fold departureForecastUpdate events for one airport into the rows. Pure. */
export function mergeDepartureEvents(prev, events) {
  const out = [...prev];
  for (const ev of events) {
    const key = rowKey(ev.icaoAddress, ev.callsign, ev.sessionStartAt);
    const idx = out.findIndex(r => rowKey(r.icaoAddress, r.callsign, r.sessionStartAt) === key);
    const merged = idx >= 0 ? { ...out[idx] } : {
      icaoAddress: ev.icaoAddress,
      callsign: ev.callsign,
      iataFlightNumber: ev.iataFlightNumber,
      airlineIcao: ev.airlineIcao,
      registration: ev.registration,
      aircraftType: ev.aircraftType,
      sessionStartAt: ev.sessionStartAt,
      forecastCount: 0,
      // A session first seen over the WebSocket: this is its first estimate.
      firstEttTime: ev.eventType === 'LIFTOFF' ? null : ev.ettTime,
    };
    merged.lastForecastAt = ev.forecastAt;
    merged.lastTaxiOutMinutes = ev.taxiOutMinutes;
    merged.ettTime = ev.ettTime;
    merged.lastLatitude = ev.latitude;
    merged.lastLongitude = ev.longitude;
    merged.runwayThreshold = ev.runwayThreshold;
    merged.runwayDistanceM = ev.runwayDistanceM;
    // Late-arriving metadata — if the row was created with these fields null and a later event
    // resolves them, fill in.
    if (!merged.callsign && ev.callsign)             merged.callsign = ev.callsign;
    if (!merged.iataFlightNumber && ev.iataFlightNumber) merged.iataFlightNumber = ev.iataFlightNumber;
    if (!merged.airlineIcao && ev.airlineIcao)       merged.airlineIcao = ev.airlineIcao;
    if (!merged.aircraftType && ev.aircraftType)     merged.aircraftType = ev.aircraftType;
    if (!merged.registration && ev.registration)     merged.registration = ev.registration;
    if (!merged.destinationIata && ev.destinationIata) merged.destinationIata = ev.destinationIata;
    if (merged.isHeavy == null && ev.isHeavy != null) merged.isHeavy = ev.isHeavy;
    if (ev.lastUpdateAt)        merged.lastUpdateAt = ev.lastUpdateAt;
    if (ev.lastSurfaceSpeedKnots != null) merged.lastSurfaceSpeedKnots = ev.lastSurfaceSpeedKnots;
    if (ev.lastHeadingDegrees != null)    merged.lastHeadingDegrees = ev.lastHeadingDegrees;
    if (ev.state)               merged.state = ev.state;
    merged.forecastCount = (merged.forecastCount || 0) + 1;
    if (ev.eventType === 'LIFTOFF') {
      merged.actualLiftoffAt = ev.forecastAt;
      merged.finalErrorMinutes = ev.finalErrorMinutes;
    }
    if (idx >= 0) out[idx] = merged; else out.unshift(merged);
  }
  out.sort((a, b) => (b.sessionStartAt || '').localeCompare(a.sessionStartAt || ''));
  return out;
}

/**
 * Counters for the ground picture, in lifecycle order. On Ground passes its aircraft
 * (AirportViewService.groundState); Departures passes its sessions and the On Ground feed for the
 * arrivals taxiing in, which it does not track itself. `cls` is the GroundStateBadge colour.
 */
export function groundCounts({ onGround = [], departures = null, nowMs = Date.now() }) {
  const ground = {};
  for (const a of onGround) {
    const s = String(a.groundState || '').toLowerCase();
    const k = s === 'taxiing' ? 'taxiing_out' : s;
    ground[k] = (ground[k] || 0) + 1;
  }
  if (!departures) {
    return [
      { label: 'Taxiing In', value: ground.taxiing_in || 0, cls: 'taxi-in' },
      { label: 'Pushback', value: ground.pushback || 0, cls: 'pushback' },
      { label: 'Engines On', value: ground.engines_on || 0, cls: 'engines-on' },
      { label: 'Taxiing Out', value: ground.taxiing_out || 0, cls: 'taxi-out' },
      { label: 'Holding', value: ground.holding || 0, cls: 'holding' },
      { label: 'Parked', value: ground.parked || 0, cls: 'parked' },
    ];
  }
  const dep = {};
  for (const r of departures) {
    const s = effectiveState(r, nowMs);
    dep[s] = (dep[s] || 0) + 1;
  }
  return [
    { label: 'Taxiing In', value: ground.taxiing_in || 0, cls: 'taxi-in' },
    { label: 'Engines On', value: dep.POWER_ON || 0, cls: 'engines-on' },
    { label: 'Taxiing Out', value: dep.TAXIING || 0, cls: 'taxi-out' },
    { label: 'Take-off', value: dep.TAKE_OFF || 0, cls: 'take-off' },
    { label: 'Departed', value: dep.DEPARTED || 0, cls: 'departed' },
  ];
}

/**
 * Splits the Departures table's rows into its two divider groups, keeping the incoming sort within
 * each: Active (still on the ground with engines on: POWER_ON, TAXIING, TAKE_OFF) first, then
 * Departed (everything after it: DEPARTED, and PARKED when shown). Reads the row's `_effState` when
 * the table has already worked it out, otherwise effectiveState().
 */
export function groupDepartureRows(rows, nowMs) {
  const active = [];
  const departed = [];
  for (const r of rows) {
    const state = r._effState || effectiveState(r, nowMs);
    (isActiveDeparture(state) ? active : departed).push(r);
  }
  return { active, departed };
}
